import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { 
  FaArrowLeft, FaArrowRight, FaPlus, FaEdit, FaTrash, FaBox, FaList, 
  FaSignOutAlt, FaChartBar, FaUsers, FaCog, FaEye, FaSearch, FaBell, FaMapMarkerAlt
} from 'react-icons/fa';
import * as api from '../../services/api';
import './ProductManagement.css';

function CategoryManagement() {
  const { adminUser, logout } = useAdmin();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [productCounts, setProductCounts] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'FaBox',
    slug: ''
  });

  const iconOptions = [
    'FaPills', 'FaTint', 'FaFlask', 'FaBandAid', 
    'FaStethoscope', 'FaMicroscope', 'FaBox', 'FaHeartbeat'
  ];

  useEffect(() => {
    loadData();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesData, productsData] = await Promise.all([
        api.getCategories(),
        api.getProducts()
      ]);
      
      console.log('Categories data:', categoriesData);
      console.log('Products data:', productsData);
      
      setCategories(categoriesData);
      
      // Count products per category
      const counts = {};
      productsData.forEach(product => {
        const categoryId = product.category_id;
        // Only count once using the actual category_id
        counts[categoryId] = (counts[categoryId] || 0) + 1;
      });
      
      console.log('Product counts:', counts);
      setProductCounts(counts);
      setError('');
    } catch (err) {
      setError('Failed to load data. Make sure the backend server is running.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-');
      if (editingCategory) {
        await api.updateCategory(editingCategory.id, { ...formData, slug });
      } else {
        await api.createCategory({ ...formData, slug });
      }
      resetForm();
      await loadData();
    } catch (err) {
      alert('Failed to save category: ' + err.message);
      console.error('Error saving category:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      icon: category.icon,
      slug: category.slug
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const productCount = productCounts[id] || 0;
    if (productCount > 0) {
      alert(`Cannot delete category. It has ${productCount} product(s). Please delete or move the products first.`);
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        setLoading(true);
        await api.deleteCategory(id);
        await loadData();
      } catch (err) {
        alert('Failed to delete category: ' + err.message);
        console.error('Error deleting category:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', icon: 'FaBox', slug: '' });
    setEditingCategory(null);
    setShowModal(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="admin-dashboard">
      {/* Modern Sidebar */}
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <div className="logo-container">
            <img src="/pharmacare-logo.png" alt="Pharmacare" className="admin-logo" />
            <div className="logo-text">
              <h2>Pharmacare</h2>
              <span>Admin Panel</span>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <h3>Overview</h3>
            <Link to="/admin" className="nav-item">
              <FaChartBar />
              <span>Dashboard</span>
              <FaArrowRight />
            </Link>
          </div>

          <div className="nav-section">
            <h3>Management</h3>
            <Link to="/admin/products" className="nav-item">
              <FaBox />
              <span>Products</span>
              <FaArrowRight />
            </Link>
            <div className="nav-item active">
              <FaList />
              <span>Categories</span>
              <FaArrowRight />
            </div>
            <Link to="/admin/branches" className="nav-item">
              <FaMapMarkerAlt />
              <span>Branches</span>
              <FaArrowRight />
            </Link>
          </div>

          <div className="nav-section">
            <h3>System</h3>
            <div className="nav-item">
              <FaUsers />
              <span>Users</span>
              <span className="badge">Soon</span>
            </div>
            <div className="nav-item">
              <FaCog />
              <span>Settings</span>
              <span className="badge">Soon</span>
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="admin-main">
        {/* Top Header */}
        <div className="admin-topbar">
          <div className="topbar-left">
            <h1>Category Management</h1>
            <p>Organize your product categories</p>
          </div>
          <div className="topbar-right">
            <div className="search-box">
              <FaSearch />
              <input type="text" placeholder="Search categories..." />
            </div>
            <div className="notifications">
              <FaBell />
            </div>
            <div className="user-menu">
              <div className="user-avatar">
                {adminUser?.username?.charAt(0).toUpperCase()}
              </div>
              <div className="user-info">
                <span>{adminUser?.username}</span>
                <small>Administrator</small>
              </div>
              <button onClick={handleLogout} className="logout-btn">
                <FaSignOutAlt />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="dashboard-content">
          <div className="page-header">
            <div className="page-header-left">
              <Link to="/admin" className="back-button">
                <FaArrowLeft /> Back to Dashboard
              </Link>
            </div>
            <div className="page-header-right">
              <button 
                onClick={loadData} 
                className="refresh-btn" 
                style={{ 
                  marginRight: '1rem', 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                  <path d="M21 3v5h-5"/>
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                  <path d="M3 21v-5h5"/>
                </svg>
                Refresh Data
              </button>
              <button onClick={() => setShowModal(true)} className="add-product-btn">
                <FaPlus /> Add Category
              </button>
            </div>
          </div>

          <div className="content-container">
            {error && (
              <div className="error-banner">
                {error}
              </div>
            )}
            {loading && !error ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading categories...</p>
              </div>
            ) : (
              <div className="products-grid">
                {categories.length === 0 ? (
                  <div className="empty-state">
                    <FaList className="empty-icon" />
                    <h3>No Categories Yet</h3>
                    <p>Start organizing your products by creating your first category.</p>
                    <button onClick={() => setShowModal(true)} className="add-first-product-btn">
                      <FaPlus /> Add Your First Category
                    </button>
                  </div>
                ) : (
                  categories.map(category => (
                    <div key={category.id} className="product-card">
                      <div className="product-image">
                        <div className="category-icon">
                          <FaList />
                        </div>
                      </div>
                      <div className="product-info">
                        <h3>{category.name}</h3>
                        <p className="product-category">{category.description}</p>
                        <div className="category-stats">
                          <span className="product-count">
                            {productCounts[category.id] || 
                             productCounts[String(category.id)] || 
                             productCounts[parseInt(category.id)] || 
                             0} products
                          </span>
                        </div>
                      </div>
                      <div className="product-actions">
                        <button onClick={() => handleEdit(category)} className="edit-btn" disabled={loading}>
                          <FaEdit />
                        </button>
                        <button onClick={() => handleDelete(category.id)} className="delete-btn" disabled={loading}>
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Category Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter category name"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  placeholder="Enter category description"
                  rows="3"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '2px solid #e0e0e0', fontSize: '1rem' }}
                />
              </div>

              <div className="form-group">
                <label>Icon</label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  required
                >
                  {iconOptions.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={resetForm} className="cancel-btn" disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Add')} Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryManagement;

